import { useEffect, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useLocation } from 'react-router-dom';
import { getTenantIdentifier, isValidTenantId } from './subdomain';
import { fetchBootstrapConfig, bootstrapKeys } from '../api/bootstrap.api';
import { useTenantStore } from '../stores/tenant.store';
import { useThemeStore } from '../stores/theme.store';
import { useAuthStore } from '../stores/auth.store';
import { SplashScreen } from '../components/loaders';

interface BootstrapProviderProps {
	children: ReactNode;
	fallback?: ReactNode;
}

const BOOTSTRAP_CACHE_KEY = 'kolab_bootstrap_cache';
const BOOTSTRAP_CACHE_VERSION = '1.0';
const BOOTSTRAP_CACHE_TTL = 15 * 60 * 1000;

interface BootstrapCache {
	version: string;
	tenantId: string;
	data: any;
	timestamp: number;
}

function getCachedBootstrap(tenantId: string): { data: any; isFresh: boolean } | null {
	try {
		const cached = localStorage.getItem(BOOTSTRAP_CACHE_KEY);
		if (!cached) return null;

		const parsedCache: BootstrapCache = JSON.parse(cached);

		if (
			parsedCache.version === BOOTSTRAP_CACHE_VERSION &&
			parsedCache.tenantId === tenantId &&
			parsedCache.data
		) {
			const isFresh = Date.now() - parsedCache.timestamp < BOOTSTRAP_CACHE_TTL;
			return { data: parsedCache.data, isFresh };
		}

		return null;
	} catch {
		return null;
	}
}

function setCachedBootstrap(tenantId: string, data: any): void {
	try {
		const cache: BootstrapCache = {
			version: BOOTSTRAP_CACHE_VERSION,
			tenantId,
			data,
			timestamp: Date.now(),
		};
		localStorage.setItem(BOOTSTRAP_CACHE_KEY, JSON.stringify(cache));
	} catch {
		// Silently fail if localStorage is unavailable
	}
}

function initFromCacheSync(
	tenantId: string,
	setTenant: Function,
	setTheme: Function
): boolean {
	const cached = getCachedBootstrap(tenantId);
	if (!cached) return false;

	try {
		const { data } = cached;
		setTenant({
			id: data.tenant?.id || data.id,
			subdomain: data.tenant?.subdomain || data.subdomain || tenantId,
			name: data.tenant?.name || data.name,
			displayName: data.tenant?.displayName || data.displayName || data.name,
			logo: data.tenant?.logo || data.logo,
			logoLight: data.tenant?.logoLight || data.logoLight,
			logoDark: data.tenant?.logoDark || data.logoDark,
			favicon: data.tenant?.favicon || data.favicon,
			metadata: data.tenant?.metadata || data.metadata,
		});

		if (data.theme) {
			setTheme(data.theme.tokens, data.theme.mode || 'light');
		}

		return true;
	} catch {
		localStorage.removeItem(BOOTSTRAP_CACHE_KEY);
		return false;
	}
}

/**
 * BootstrapProvider - Handles tenant detection, config fetching, and theme initialization.
 * Does NOT handle module loading or federation runtime.
 */
export function BootstrapProvider({ children, fallback }: BootstrapProviderProps) {
	const location = useLocation();

	const { setTenant } = useTenantStore();
	const { setTheme } = useThemeStore();
	const authStore = useAuthStore();

	const isLoginPage = location.pathname === '/login';
	const tenantId = getTenantIdentifier();

	const [cacheState] = useState(() => {
		if (!tenantId) return { loaded: false, fresh: false };
		const loaded = initFromCacheSync(tenantId, setTenant, setTheme);
		const cached = loaded ? getCachedBootstrap(tenantId) : null;
		return { loaded, fresh: cached?.isFresh ?? false };
	});

	const [isReady, setIsReady] = useState(cacheState.loaded);
	const [error, setError] = useState<string | null>(null);

	useEffect(() => {
		authStore.initialize();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		if (tenantId && !isValidTenantId(tenantId)) {
			setError('Invalid tenant identifier');
		}
	}, [tenantId]);

	const queryEnabled =
		!!tenantId && !error && !cacheState.fresh && (isLoginPage || authStore.isAuthenticated);

	const {
		data: config,
		isLoading,
		isFetching,
		error: queryError,
	} = useQuery({
		queryKey: bootstrapKeys.config(tenantId),
		queryFn: async () => {
			const data = await fetchBootstrapConfig(tenantId);
			if (tenantId) {
				setCachedBootstrap(tenantId, data);
			}
			return data;
		},
		enabled: queryEnabled,
		retry: 1,
		staleTime: BOOTSTRAP_CACHE_TTL,
		refetchOnMount: false,
		refetchOnWindowFocus: false,
	});

	useEffect(() => {
		if (!config) return;

		try {
			setTenant({
				id: config.id,
				subdomain: config.subdomain || tenantId || '',
				name: config.name,
				displayName: config.displayName || config.name,
				logo: config.logo,
				logoLight: config.logoLight,
				logoDark: config.logoDark,
				favicon: config.favicon,
				metadata: config.metadata,
			});

			if (config.theme) {
				setTheme(config.theme.tokens, config.theme.mode || 'light');
			}

			if (!isReady) {
				setIsReady(true);
			}
		} catch (err) {
			setError(err instanceof Error ? err.message : 'Failed to initialize application');
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [config, tenantId]);

	if (error || queryError) {
		if (isLoginPage) {
			return <>{children}</>;
		}

		return (
			<div className="min-h-screen flex items-center justify-center bg-gray-50">
				<div className="text-center max-w-md p-8">
					<h1 className="text-2xl font-bold text-red-600 mb-4">Configuration Error</h1>
					<p className="text-gray-700 mb-4">
						{error || (queryError as Error)?.message || 'Failed to load configuration'}
					</p>
					{!tenantId && (
						<p className="text-sm text-gray-500 mt-4">
							No tenant identifier found. Please access via subdomain (e.g., tenant1.example.com, or
							tenant1.localhost for local development).
						</p>
					)}
					<button
						onClick={() => window.location.reload()}
						className="mt-6 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
					>
						Retry
					</button>
				</div>
			</div>
		);
	}

	if (authStore.isInitialized && !authStore.isAuthenticated && !isLoginPage) {
		return <>{children}</>;
	}

	if (!isReady && (isLoading || isFetching)) {
		return <>{fallback || <SplashScreen />}</>;
	}

	if (isLoginPage) {
		return <>{children}</>;
	}

	return <>{children}</>;
}
